import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  DynamicFormComponent,
  checkbox,
  customField,
  formBuilder,
  header,
  select,
  text,
  textarea,
} from '@kinet-open-network/client/core/forms/ui';
import {
  AutomationContext,
  AutomationTrigger,
  IAutomationData,
  automationContextOptions,
  automationTriggerByContext,
  automationTriggerOptions,
  createEnumList,
} from '@kinet-open-network/shared/data-access/model';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { AutomationActionBuilderTypeComponent } from './automation-actions/automation-action-builder-type.component';
import { AutomationConditionBuilderTypeComponent } from './automation-conditions/automation-condition-builder-type.component';
import { AutomationTriggerBuilderTypeComponent } from './automation-trigger/automation-trigger-builder-type.component';

@Component({
  selector: 'kon-automation-builder',
  imports: [InputTextModule, FormsModule, CheckboxModule, DynamicFormComponent],
  providers: [],
  template: `
    <kon-dynamic-form
      [fields]="fields"
      [model]="automation()"
      [loading]="loading()"
      (modelChange)="handleModelChange($event)"
      (submitted)="save.emit()"
    />
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomationBuilderComponent {
  readonly automation = model<Partial<IAutomationData>>({
    enabled: true,
    context: 'customer',
    trigger: AutomationTrigger.Cron,
  });

  readonly loading = input<boolean>(false);

  readonly save = output<void>();

  readonly context = computed(() => this.automation().context);

  readonly enabledTriggers = computed(() => {
    const ctx = this.context();

    if (!ctx) return [];

    return automationTriggerByContext[ctx];
  });

  readonly triggerOptions = computed(() => {
    const enabledTriggers = this.enabledTriggers();

    return createEnumList(automationTriggerOptions).filter((t) =>
      enabledTriggers?.includes(t.value as AutomationTrigger),
    );
  });

  readonly fields = formBuilder([
    header('Automatizácia', 'Základné informácie o automatizácii'),
    checkbox('enabled', 'Automatizácia povolená'),
    text('name', 'Názov').props({
      placeholder: 'Názov automatizácie (napr. Upomienka)',
    }),
    textarea('description', 'Popis').props({
      placeholder: 'Popis automatizácie',
    }),
    select('context', 'Kontext')
      .options(createEnumList(automationContextOptions))
      .required()
      .change((f) =>
        this.resetContextDependentFields(
          f.formControl?.value as AutomationContext,
        ),
      ),
    header('Spúšťanie', 'Nastavte, ako sa má automatizácia spúšťať'),
    select('trigger', 'Spúšťač')
      .options(toObservable(this.triggerOptions))
      .required(),
    customField('triggerData', AutomationTriggerBuilderTypeComponent)
      .props({ label: 'Argumenty spúšťača' })
      .wrappers(['form-field']),
    header(
      'Podmienky',
      'Podmienky, ktoré musia byť splnené pre vykonanie akcií',
    ),
    customField('conditions', AutomationConditionBuilderTypeComponent)
      .wrappers(['form-field'])
      .expressions({
        'props.context': toObservable(this.context),
      }),
    header('Akcie', 'Nastavte akcie, ktoré sa majú vykonať'),
    customField('actions', AutomationActionBuilderTypeComponent)
      .wrappers(['form-field'])
      .expressions({
        'props.context': toObservable(this.context),
      }),
  ]);

  handleModelChange(model: Partial<IAutomationData>) {
    this.automation.update((prev) => ({ ...prev, ...model }));
  }

  private resetContextDependentFields(context?: AutomationContext) {
    const shouldResetTrigger = context
      ? !automationTriggerByContext?.[context]?.includes(
          this.automation().trigger as AutomationTrigger,
        ) || this.automation().trigger === AutomationTrigger.FieldChange
      : true;

    this.automation.update((prev) => ({
      ...prev,
      context: context ?? prev.context,
      trigger: shouldResetTrigger ? undefined : prev.trigger,
      triggerData: shouldResetTrigger ? undefined : prev.triggerData,
      conditions: undefined,
      actions: [],
    }));
  }
}
